<template>
  <Grid
    class="companies-grid"
    :pageable="pageable"
    :data-items="companies"
    :skip="skip"
    :take="take"
    :total="totalCompanies"
    :columns="columns"
    :loader="companiesLoading"
    :filter="filter"
    :column-menu="!companiesLoading"
    :resizable="true"
    @columnresize="onColumnResize"
    @filterchange="filterChangeHandler"
    @sortchange="sortChangeHandler"
    @pagechange="pageChangeHandler"
    data-cy="companies-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="columns"
        :getItems="getPropValues"
        @sortchange="(e) => props.onSortchange(e)"
        @filterchange="(e) => props.onFilterchange(e)"
        @closemenu="(e) => props.onClosemenu(e)"
        @contentfocus="(e) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
  </Grid>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridColumnProps,
  type GridColumnResizeEvent,
  type GridFilterChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
} from "@progress/kendo-vue-grid";
import { ref, watchEffect } from "vue";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import type { SortOptions } from "@/Domain/SortOptions";
import type { CompanyDisplay } from "@/Domain/CompanyDisplay";
import type { CompositeFilterDescriptor, SortDescriptor } from "@progress/kendo-data-query";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const props = defineProps<{
  companies: CompanyDisplay[];
  companiesLoading: boolean;
  pageSize: number;
  skip: number;
  totalCompanies: number;
  filter: CompositeFilterDescriptor | undefined;
  getPropValues: (column: GridColumnPropsExtended) => Promise<CompanyDisplay[]>;
}>();

const emit = defineEmits<{
  (e: "companiesChanged", options: ItemChangedOptions): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
}>();

const gridName = "CompaniesGrid";

const skip = ref<number>(props.skip);
const take = ref<number>(props.pageSize);

const columns = ref<GridColumnProps[]>([
  {
    title: "Company",
    field: "Name",
    columnMenu: "columnMenu",
  },
  {
    title: "Company Category",
    field: "CompanyCategory",
    columnMenu: "columnMenu",
  },
  {
    title: "State",
    field: "_Address.state",
    columnMenu: "columnMenu",
  },
  {
    title: "Country",
    field: "_Address.country",
    columnMenu: "columnMenu",
  },
  {
    title: "RC",
    field: "Rc",
    columnMenu: "columnMenu",
  },
  {
    title: "RC Email",
    field: "RcEmail",
    columnMenu: "columnMenu",
  },
  {
    title: "RC Phone",
    field: "RcPhone",
    columnMenu: "columnMenu",
  },
  {
    title: "RC Cell",
    field: "RcCell",
    columnMenu: "columnMenu",
  },
]);

let pageSizes = [10, 20, 25, 50, 100];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);
// let pageSize = take.value;
const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});

GridOptionsService.loadGridOptions(gridName, columns.value);

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("companiesChanged", {
    skip: 0,
    take: take.value,
    filterEvent: e,
  });
};

const sortChangeHandler = function (e: any) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("companiesChanged", {
    skip: event.page.skip,
    take: event.page.take,
    filterEvent: null,
  });
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) {
    columns.value = columnsProps;
    GridOptionsService.saveVisibleColumns(gridName, columnsProps);
  }
  console.debug("CompaniesGrid.vue, onColumnSubmit", columnsProps);
};

const onColumnResize = function (e: GridColumnResizeEvent) {
  const col = GridOptionsService.getResizedColumn(e, columns.value);
  if (col.title)
    GridOptionsService.saveColumnWidth(gridName, col.title, e.newWidth);
};

watchEffect(() => {
  skip.value = props.skip;
});

watchEffect(() => {
  take.value = props.pageSize;
});
</script>

<style scoped lang="scss">
.companies-grid {
  min-height: 450px;
}
</style>
